import { useLocation, Link, useNavigate } from "react-router-dom";

import { useState, useEffect, useMemo } from "react";
import {
  LayoutDashboard,
  TrendingUp,
  UserCheck,
  Package,
  Zap,
  MessageCircle,
  Megaphone,
  BarChart3,
  Layers,
  Truck,
  Handshake,
  Settings2,
  GitBranch,
  LogOut,
  Shield,
  ChevronDown,
  HardHat,
  CreditCard,
  Lock,
  FolderKanban,
  ClipboardList,
  Search,
  Sparkles,
  Filter,
  Plus,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { getRoleLabel } from "@/hooks/useRolePermissions";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useTenantSettings } from "@/hooks/useTenantSettings";
import { useTenantPlan, type FeatureKey } from "@/hooks/useTenantPlan";
import { FeatureLockedModal } from "@/components/billing/FeatureLockedModal";
import { FEATURE_CATALOG } from "@/components/billing/plan-constants";
import { QuickQuoteFormDialog } from "@/components/cotacoes/QuickQuoteFormDialog";
import { QuickQuoteCreatedDialog } from "@/components/cotacoes/QuickQuoteCreatedDialog";
import type { QuickQuote } from "@/hooks/useQuickQuotes";

type PermissionKey =
  | "canViewDashboard"
  | "canViewCRM"
  | "canViewInstalacoes"
  | "canViewFinanceiro"
  | "canViewConfiguracoes"
  | "canViewStatus"
  | "canViewClientes"
  | "canViewIntegracoes"
  | "canManageTenant"
  | "canManageCampaigns";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ElementType;
  permission: PermissionKey;
  featureKey?: FeatureKey;
  hideIfLocked?: boolean;
}

const STORAGE_KEY = "sidebar_cadastros_open";

export function AppSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { profile, currentTenant, currentRole, permissions, signOut } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const { getSetting } = useTenantSettings();
  const { hasFeature, planName, isTrialing, daysUntilExpiry, isLoading: isPlanLoading } = useTenantPlan();
  const brandLogoUrl = getSetting("brand_logo_url", currentTenant?.logo_url || "");
  const isCollapsed = state === "collapsed";

  const [cadastrosOpen, setCadastrosOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(cadastrosOpen));
    } catch {}
  }, [cadastrosOpen]);

  // Menu groups with feature keys
  const vendasItems: MenuItem[] = [
    { title: "CRM", url: "/app/crm", icon: TrendingUp, permission: "canViewCRM", featureKey: "crm" },
    { title: "Atividades", url: "/app/atividades", icon: ClipboardList, permission: "canViewCRM", featureKey: "atividades" },
    { title: "Mensagens", url: "/app/mensagens", icon: MessageCircle, permission: "canViewCRM", featureKey: "whatsapp_hub" },
    { title: "Campanhas", url: "/app/campanhas-whatsapp", icon: Megaphone, permission: "canManageCampaigns", featureKey: "whatsapp_hub", hideIfLocked: true },
    { title: "Segmentos", url: "/app/campanhas-whatsapp/segmentos", icon: Filter, permission: "canManageCampaigns", featureKey: "whatsapp_hub", hideIfLocked: true },
    { title: "Automações", url: "/app/automacoes-whatsapp", icon: Zap, permission: "canViewCRM", featureKey: "whatsapp_hub", hideIfLocked: true },
    { title: "Prospecção", url: "/app/prospeccao", icon: Search, permission: "canViewCRM", featureKey: "prospeccao_bdr", hideIfLocked: true },
  ];

  const operacoesItems: MenuItem[] = [
    { title: "Projetos", url: "/app/projetos", icon: FolderKanban, permission: "canManageTenant" },
    { title: "Logística", url: "/app/logistica", icon: Package, permission: "canViewInstalacoes", featureKey: "logistica" },
    { title: "Instalações", url: "/app/instalacoes", icon: Zap, permission: "canViewInstalacoes", featureKey: "instalacoes" },
  ];

  const cadastrosItems: MenuItem[] = [
    { title: "Produtos & Serviços", url: "/app/produtos-servicos", icon: Layers, permission: "canViewCRM" },
    { title: "Contatos", url: "/app/cadastros/contatos", icon: UserCheck, permission: "canViewCRM" },
  ];

  const sistemaItems: MenuItem[] = [
    { title: "Novidades", url: "/app/novidades", icon: Sparkles, permission: "canViewDashboard" },
    { title: "Minha Conta", url: "/app/minha-conta", icon: CreditCard, permission: "canViewConfiguracoes" },
    { title: "Configurações", url: "/app/configuracoes", icon: Settings2, permission: "canViewConfiguracoes" },
    { title: "Integrações", url: "/app/integracoes", icon: GitBranch, permission: "canViewIntegracoes" },
  ];

  const [lockedModal, setLockedModal] = useState<{ open: boolean; featureKey: FeatureKey; label: string }>({
    open: false,
    featureKey: "crm",
    label: "",
  });

  // ─── Cotação Rápida (Quick Action no topo da sidebar) ───
  const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
  const [createdQuote, setCreatedQuote] = useState<QuickQuote | null>(null);
  const canCreateQuickQuote =
    !!permissions.canCreateDeal &&
    (hasFeature("cotacao_rapida") || isSuperAdmin);

  const navigate = useNavigate();

  const filterItems = (items: MenuItem[]) =>
    items.filter((item) => {
      if (!permissions[item.permission]) return false;
      if (
        item.hideIfLocked &&
        item.featureKey &&
        !hasFeature(item.featureKey) &&
        !isSuperAdmin
      ) return false;
      return true;
    });

  const filteredVendas = useMemo(() => filterItems(vendasItems), [permissions, hasFeature, isSuperAdmin]);
  const filteredOperacoes = useMemo(() => filterItems(operacoesItems), [permissions, hasFeature, isSuperAdmin]);
  const filteredCadastros = useMemo(() => filterItems(cadastrosItems), [permissions, hasFeature, isSuperAdmin]);
  const filteredSistema = useMemo(() => filterItems(sistemaItems), [permissions, hasFeature, isSuperAdmin]);

  const showFinanceiro = permissions.canViewFinanceiro;

  const isActive = (url: string) => {
    if (url === "/app") return location.pathname === "/app";
    return location.pathname.startsWith(url);
  };

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getTruncatedName = (name: string | null) => {
    if (!name) return "Solux CRM";
    return name.split(" ").slice(0, 2).join(" ");
  };

  const getRoleRingClass = (role: string | null) => {
    switch (role) {
      case "admin": return "ring-2 ring-primary";
      case "vendedor": return "ring-2 ring-blue-400";
      case "instalador": return "ring-2 ring-amber-400";
      default: return "ring-2 ring-primary/50";
    }
  };

  const renderMenuItems = (items: MenuItem[]) =>
    items.map((item) => {
      // While plan is loading, render optimistically (no lock) to avoid flicker
      const isLocked = !isPlanLoading && !!item.featureKey && !hasFeature(item.featureKey);

      if (isLocked) {
        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              tooltip={`${item.title} — Disponível em planos superiores`}
              className="opacity-60 cursor-pointer"
              onClick={() =>
                setLockedModal({
                  open: true,
                  featureKey: item.featureKey!,
                  label: item.title,
                })
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
              <Lock className="ml-auto h-3.5 w-3.5 text-amber-500" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
            <Link to={item.url}>
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });

  // Plan badge label
  const planBadgeLabel = isTrialing
    ? `Trial${daysUntilExpiry !== null ? ` - ${daysUntilExpiry}d` : ""}`
    : planName && planName !== "Sem plano"
    ? planName
    : "PRO";

  return (
    <>
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Header / Logo */}
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <Link to="/app" className="flex items-center gap-3">
          {brandLogoUrl ? (
            <img src={brandLogoUrl} alt="Logo" className="h-9 w-9 shrink-0 rounded-full object-cover shadow-md ring-1 ring-white/20" />
          ) : isCollapsed ? (
            <img src="/brand/Solux-v3-512x512.png" alt="Solux CRM" className="h-8 w-8 shrink-0 rounded-full object-contain" />
          ) : (
            <img src="/brand/Solux-v2-1200x500.png" alt="Solux CRM" className="h-9 w-auto shrink-0" />
          )}
          {!isCollapsed && (
            <div className="flex flex-col gap-0.5 overflow-hidden">
              <span className="truncate text-sm font-bold text-sidebar-foreground max-w-[140px]">
                {getTruncatedName(currentTenant?.name)}
              </span>
              <Badge
                variant={isTrialing ? "outline" : "secondary"}
                className="w-fit text-[9px] px-1.5 py-0 rounded-full bg-[#F59E0B] text-white border-0"
              >
                {planBadgeLabel}
              </Badge>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {/* Quick Action — Nova Cotação */}
        {canCreateQuickQuote && (
          <SidebarGroup>
            <SidebarGroupContent>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      onClick={() => setQuickQuoteOpen(true)}
                      className="mx-auto flex h-9 w-9 items-center justify-center"
                      aria-label="Nova Cotação"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Nova Cotação</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  onClick={() => setQuickQuoteOpen(true)}
                  className="w-full justify-start gap-2 font-semibold shadow-sm"
                  size="default"
                >
                  <Plus className="h-4 w-4" />
                  Nova Cotação
                </Button>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Dashboard (solo) */}
        {permissions.canViewDashboard && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/app")} tooltip="Dashboard">
                    <Link to="/app">
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Vendas */}
        {filteredVendas.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Vendas</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{renderMenuItems(filteredVendas)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Operações */}
        {filteredOperacoes.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Operações</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{renderMenuItems(filteredOperacoes)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Financeiro (solo) */}
        {showFinanceiro && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  {isPlanLoading || hasFeature("financeiro") ? (
                    <SidebarMenuButton asChild isActive={isActive("/app/financeiro")} tooltip="Financeiro">
                      <Link to="/app/financeiro">
                        <BarChart3 className="h-4 w-4" />
                        <span>Financeiro</span>
                      </Link>
                    </SidebarMenuButton>
                  ) : (
                    <SidebarMenuButton
                      tooltip="Financeiro — Disponível em planos superiores"
                      className="opacity-60 cursor-pointer"
                      onClick={() =>
                        setLockedModal({
                          open: true,
                          featureKey: "financeiro",
                          label: "Financeiro",
                        })
                      }
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Financeiro</span>
                      <Lock className="ml-auto h-3.5 w-3.5 text-amber-500" />
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Cadastros (collapsible) */}
        {filteredCadastros.length > 0 && (
          <Collapsible open={cadastrosOpen} onOpenChange={setCadastrosOpen}>
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer select-none hover:text-sidebar-foreground transition-colors">
                  Cadastros
                  <ChevronDown className={cn(
                    "ml-auto h-3.5 w-3.5 transition-transform duration-200",
                    cadastrosOpen && "rotate-180"
                  )} />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>{renderMenuItems(filteredCadastros)}</SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Sistema */}
        {filteredSistema.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Sistema</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{renderMenuItems(filteredSistema)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Super Admin */}
        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Administração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Super Admin">
                    <Link to="/admin">
                      <Shield className="h-4 w-4" />
                      <span>Super Admin</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className={cn("flex items-center gap-3", isCollapsed && "justify-center")}>
          <Avatar className={cn("h-9 w-9", getRoleRingClass(currentRole))}>
            <AvatarImage src={profile?.avatar_url || undefined} />
            <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground text-xs">
              {getInitials(profile?.full_name)}
            </AvatarFallback>
          </Avatar>
          {!isCollapsed && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <span className="truncate text-sm font-medium text-sidebar-foreground">
                {profile?.full_name || "Usuário"}
              </span>
              <span className="text-[10px] text-sidebar-foreground/50">
                {getRoleLabel(currentRole)}
              </span>
            </div>
          )}
          {!isCollapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={signOut}
                  className="h-8 w-8 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Sair</TooltipContent>
            </Tooltip>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>

      <FeatureLockedModal
        open={lockedModal.open}
        onOpenChange={(open) => setLockedModal((prev) => ({ ...prev, open }))}
        featureKey={lockedModal.featureKey}
        featureLabel={lockedModal.label}
      />

      {/* Quick Quote — wizard + modal pós-criação (apenas via sidebar) */}
      <QuickQuoteFormDialog
        open={quickQuoteOpen}
        onOpenChange={setQuickQuoteOpen}
        onSaved={(q) => {
          setQuickQuoteOpen(false);
          setCreatedQuote(q);
        }}
      />
      <QuickQuoteCreatedDialog
        open={!!createdQuote}
        onOpenChange={(open) => {
          if (!open) setCreatedQuote(null);
        }}
        quote={createdQuote}
      />
    </>
  );
}
